"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  MCGILL_FIELDS as F,
  formatMcgill,
  type McgillKind,
  type McgillLabels,
  mcgillIsReady,
} from "@/lib/tool-kit/legal-styles/mcgill-guide-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const MCGILL_GUIDE_CITATION_GENERATOR_STRINGS = {
  formHeading: "Source details",
  resultsHeading: "Your McGill Guide citations",
  emptyPromptCase:
    "Enter the style of cause and a neutral citation, CanLII citation or reporter citation to see your footnotes.",
  emptyPromptStatute:
    "Enter the statute's title, volume, year and chapter to see your footnotes.",
  emptyPromptRegulation:
    "Enter the regulation's citation, such as SOR/91-499, to see your footnotes.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  kindLabel: "What you are citing",
  kindCase: "A case",
  kindStatute: "A statute",
  kindRegulation: "A regulation",
  mcgillLabel: "McGill Guide (10th edition)",
  footnote: "Footnote (first citation)",
  later: "Later footnote (supra)",
  ibid: "Next footnote, same source (ibid)",
  noteNumberLabel: "Footnote number of the first citation",
  noteNumberPlaceholder: "1",
  noteNumberHint:
    "Used in the later footnote, which reads supra note 1. Left blank, it reads note x for you to fill in.",
  shortFormLabel: "Short form (optional)",
  shortFormHint:
    "Added in square brackets, in italics, at the end of the first footnote and used in later ones. Left blank, later footnotes use the first-named party or the title.",
  caseNameLabel: "Style of cause",
  caseNamePlaceholder: "R v Jordan",
  caseNameHint:
    "The first-named party on each side, by last name, as the judgment is “indexed as”. R stands for the Crown. It is set in italics.",
  neutralLabel: "Neutral citation",
  neutralPlaceholder: "2016 SCC 27",
  neutralHint:
    "Year, court and number, exactly as the court gave it. If the case has one, it comes first.",
  canliiLabel: "CanLII citation (if there is no neutral citation)",
  canliiPlaceholder: "1975 CanLII 148",
  canliiHint: "Only for a decision the court did not give a neutral citation.",
  reporterLabel: "Reporter citation",
  reporterPlaceholder: "[2016] 1 SCR 631",
  reporterHint:
    "Square brackets when the year identifies the volume: [1986] 1 SCR 103. Round brackets and a comma when the volume number does: (1901), 8 BCR 225. With a neutral citation it becomes the parallel citation.",
  parallelLabel: "Another parallel citation (optional)",
  parallelPlaceholder: "26 DLR (4th) 200",
  courtLabel: "Court (CanLII and reporter citations)",
  courtPlaceholder: "SCC",
  courtHint:
    "In brackets at the end. Leave it out when the reporter's name shows the court, as SCR does. A neutral citation already names the court.",
  paragraphsLabel: "Paragraph pinpoint",
  paragraphsPlaceholder: "46",
  paragraphsHint:
    "A number, a span (46-48) or several (46, 49). Used for neutral and CanLII citations; we write at para or at paras.",
  pagesLabel: "Page pinpoint",
  pagesPlaceholder: "138-39",
  pagesHint:
    "Used for a printed reporter, and for a CanLII citation with no paragraph. Spans are kept as you type them.",
  judgeLabel: "Judge (optional)",
  judgePlaceholder: "LaForme JA",
  judgeHint:
    "After the pinpoint, with the office abbreviated: Abella J, McLachlin CJ, Binnie J, dissenting.",
  titleLabelStatute: "Title of the statute",
  titlePlaceholderStatute: "Criminal Code",
  titleHintStatute:
    "The short title, with its year if it has one (Pharmacy Act, 1991). It is set in italics.",
  seriesLabel: "Statute volume",
  seriesChoose: "Choose a volume",
  seriesRsc: "RSC — Revised Statutes of Canada",
  seriesSc: "SC — Statutes of Canada",
  seriesRsa: "RSA — Revised Statutes of Alberta",
  seriesSa: "SA — Statutes of Alberta",
  seriesRsbc: "RSBC — Revised Statutes of British Columbia",
  seriesSbc: "SBC — Statutes of British Columbia",
  seriesCcsm: "CCSM — Continuing Consolidation of the Statutes of Manitoba",
  seriesSm: "SM — Statutes of Manitoba",
  seriesRsnb: "RSNB — Revised Statutes of New Brunswick",
  seriesSnb: "SNB — Statutes of New Brunswick",
  seriesRsnl: "RSNL — Revised Statutes of Newfoundland and Labrador",
  seriesSnl: "SNL — Statutes of Newfoundland and Labrador",
  seriesRsnwt: "RSNWT — Revised Statutes of the Northwest Territories",
  seriesSnwt: "SNWT — Statutes of the Northwest Territories",
  seriesRsns: "RSNS — Revised Statutes of Nova Scotia",
  seriesSns: "SNS — Statutes of Nova Scotia",
  seriesSnu: "SNu — Statutes of Nunavut",
  seriesRso: "RSO — Revised Statutes of Ontario",
  seriesSo: "SO — Statutes of Ontario",
  seriesRspei: "RSPEI — Revised Statutes of Prince Edward Island",
  seriesSpei: "SPEI — Statutes of Prince Edward Island",
  seriesCqlr: "CQLR — Compilation of Quebec Laws and Regulations",
  seriesSq: "SQ — Statutes of Quebec",
  seriesSs: "SS — Statutes of Saskatchewan",
  seriesRsy: "RSY — Revised Statutes of the Yukon",
  seriesSy: "SY — Statutes of the Yukon",
  yearLabel: "Year of the volume",
  yearPlaceholder: "1985",
  yearHint:
    "The year of the revision for a revised volume (RSC 1985), the year of enactment for an annual one (SC 2002). CQLR and CCSM take no year.",
  chapterLabel: "Chapter",
  chapterPlaceholder: "C-46",
  chapterHint: "As printed, with its letter and punctuation: C-46, M.14, 29.",
  partsLabel: "Section or other provision (optional)",
  partsPlaceholderStatute: "s 718.2(e)",
  partsHintStatute:
    "s, ss, sch, pt, para, cl. Words such as section 5 become s 5. A span or list is kept as typed: ss 5, 7-12.",
  titleLabelRegulation: "Title (optional, but needed for CRC)",
  titlePlaceholderRegulation: "Canadian International Trade Tribunal Rules",
  citationLabel: "Regulation citation",
  citationPlaceholder: "SOR/91-499",
  citationHint:
    "SOR/91-499 or SOR/2022-105; CRC, c 879; O Reg 429/07; RRO 1990, Reg 737. For CRC we add the revision year (1978) after the pinpoint.",
  partsPlaceholderRegulation: "s 2",
  caseNote:
    "McGill Guide 10th edition: a neutral citation comes first and is followed by the pinpoint (at para) and then any parallel citation; a CanLII citation is used only when there is no neutral citation; other reports follow the order year, main citation, pinpoint, parallel citation, court. Later footnotes use supra, and the pinpoint is repeated.",
  statuteNote:
    "McGill Guide 10th edition: italic title, volume and year with no comma between them, the chapter, then the pinpoint (s, ss) with no “at”. Revised volumes carry the year of the revision; CQLR and CCSM carry none.",
  regulationNote:
    "McGill Guide 10th edition: the title in italics (mandatory for CRC), then the SOR, CRC, O Reg or RRO citation and the pinpoint; a CRC citation ends with the 1978 revision year after the pinpoint.",
  exampleCase: {
    caseName: "R v Jordan",
    neutral: "2016 SCC 27",
    reporter: "[2016] 1 SCR 631",
    paragraphs: "46",
    shortForm: "Jordan",
    noteNumber: "1",
  },
  exampleJudge: {
    caseName: "Radonna Investments Ltd v Rubin",
    neutral: "2012 ONCA 321",
    paragraphs: "2",
    judge: "LaForme JA",
    shortForm: "Radonna",
    noteNumber: "4",
  },
  exampleCanlii: {
    caseName: "Dickson v Royal Bank of Canada",
    canlii: "1975 CanLII 148",
    court: "SCC",
    pages: "836",
    noteNumber: "7",
  },
  exampleReporter: {
    caseName: "R v Oakes",
    reporter: "[1986] 1 SCR 103",
    parallel: "26 DLR (4th) 200",
    pages: "138-39",
    noteNumber: "2",
  },
  exampleStatute: {
    title: "Criminal Code",
    series: "RSC",
    year: "1985",
    chapter: "C-46",
    parts: "section 718.2(e)",
    shortForm: "Code",
    noteNumber: "3",
  },
  exampleAnnual: {
    title: "Species at Risk Act",
    series: "SC",
    year: "2002",
    chapter: "29",
    parts: "ss 5, 7-12",
  },
  exampleQuebec: {
    title: "Charter of human rights and freedoms",
    series: "CQLR",
    chapter: "C-12",
    parts: "s 10",
  },
  exampleRegulation: {
    title: "Canadian International Trade Tribunal Rules",
    citation: "SOR/91-499",
    noteNumber: "5",
  },
  exampleConsolidated: {
    title: "Migratory Birds Regulations",
    citation: "CRC, c 1035",
    parts: "s 9",
  },
  exampleOntario: {
    title: "Accessibility Standards for Customer Service",
    citation: "O Reg 429/07",
    parts: "s 2",
  },
} as const;

type Strings = typeof MCGILL_GUIDE_CITATION_GENERATOR_STRINGS;

function text(
  id: string,
  label: string,
  placeholder?: string,
  hint?: string,
  wide?: boolean
): SourceField {
  return { id, kind: "text", label, placeholder, hint, wide };
}

function caseFields(s: Strings): SourceField[] {
  return [
    text(
      F.caseName,
      s.caseNameLabel,
      s.caseNamePlaceholder,
      s.caseNameHint,
      true
    ),
    text(F.neutral, s.neutralLabel, s.neutralPlaceholder, s.neutralHint),
    text(F.canlii, s.canliiLabel, s.canliiPlaceholder, s.canliiHint),
    text(F.reporter, s.reporterLabel, s.reporterPlaceholder, s.reporterHint),
    text(F.parallel, s.parallelLabel, s.parallelPlaceholder),
    text(F.court, s.courtLabel, s.courtPlaceholder, s.courtHint),
    text(
      F.paragraphs,
      s.paragraphsLabel,
      s.paragraphsPlaceholder,
      s.paragraphsHint
    ),
    text(F.pages, s.pagesLabel, s.pagesPlaceholder, s.pagesHint),
    text(F.judge, s.judgeLabel, s.judgePlaceholder, s.judgeHint),
    text(F.shortForm, s.shortFormLabel, undefined, s.shortFormHint),
    text(
      F.noteNumber,
      s.noteNumberLabel,
      s.noteNumberPlaceholder,
      s.noteNumberHint
    ),
  ];
}

function seriesOptions(s: Strings): { value: string; label: string }[] {
  return [
    { value: "", label: s.seriesChoose },
    { value: "RSC", label: s.seriesRsc },
    { value: "SC", label: s.seriesSc },
    { value: "RSA", label: s.seriesRsa },
    { value: "SA", label: s.seriesSa },
    { value: "RSBC", label: s.seriesRsbc },
    { value: "SBC", label: s.seriesSbc },
    { value: "CCSM", label: s.seriesCcsm },
    { value: "SM", label: s.seriesSm },
    { value: "RSNB", label: s.seriesRsnb },
    { value: "SNB", label: s.seriesSnb },
    { value: "RSNL", label: s.seriesRsnl },
    { value: "SNL", label: s.seriesSnl },
    { value: "RSNWT", label: s.seriesRsnwt },
    { value: "SNWT", label: s.seriesSnwt },
    { value: "RSNS", label: s.seriesRsns },
    { value: "SNS", label: s.seriesSns },
    { value: "SNu", label: s.seriesSnu },
    { value: "RSO", label: s.seriesRso },
    { value: "SO", label: s.seriesSo },
    { value: "RSPEI", label: s.seriesRspei },
    { value: "SPEI", label: s.seriesSpei },
    { value: "CQLR", label: s.seriesCqlr },
    { value: "SQ", label: s.seriesSq },
    { value: "SS", label: s.seriesSs },
    { value: "RSY", label: s.seriesRsy },
    { value: "SY", label: s.seriesSy },
  ];
}

function statuteFields(s: Strings): SourceField[] {
  return [
    text(
      F.title,
      s.titleLabelStatute,
      s.titlePlaceholderStatute,
      s.titleHintStatute,
      true
    ),
    {
      id: F.series,
      kind: "select",
      label: s.seriesLabel,
      options: seriesOptions(s),
    },
    text(F.year, s.yearLabel, s.yearPlaceholder, s.yearHint),
    text(F.chapter, s.chapterLabel, s.chapterPlaceholder, s.chapterHint),
    text(F.parts, s.partsLabel, s.partsPlaceholderStatute, s.partsHintStatute),
    text(F.shortForm, s.shortFormLabel, undefined, s.shortFormHint),
    text(
      F.noteNumber,
      s.noteNumberLabel,
      s.noteNumberPlaceholder,
      s.noteNumberHint
    ),
  ];
}

function regulationFields(s: Strings): SourceField[] {
  return [
    text(
      F.title,
      s.titleLabelRegulation,
      s.titlePlaceholderRegulation,
      undefined,
      true
    ),
    text(F.citation, s.citationLabel, s.citationPlaceholder, s.citationHint),
    text(F.parts, s.partsLabel, s.partsPlaceholderRegulation),
    text(F.shortForm, s.shortFormLabel, undefined, s.shortFormHint),
    text(
      F.noteNumber,
      s.noteNumberLabel,
      s.noteNumberPlaceholder,
      s.noteNumberHint
    ),
  ];
}

function fieldsFor(s: Strings, kind: McgillKind): SourceField[] {
  switch (kind) {
    case "case":
      return caseFields(s);
    case "statute":
      return statuteFields(s);
    default:
      return regulationFields(s);
  }
}

function exampleFor(s: Strings, kind: McgillKind): SourceValues {
  switch (kind) {
    case "case":
      return s.exampleCase;
    case "statute":
      return s.exampleStatute;
    default:
      return s.exampleRegulation;
  }
}

function emptyPromptFor(s: Strings, kind: McgillKind): string {
  switch (kind) {
    case "case":
      return s.emptyPromptCase;
    case "statute":
      return s.emptyPromptStatute;
    default:
      return s.emptyPromptRegulation;
  }
}

/** The widget's strings, as the formatter's labels. */
export function mcgillLabels(s: Strings): McgillLabels {
  return {
    mcgill: s.mcgillLabel,
    footnote: s.footnote,
    later: s.later,
    ibid: s.ibid,
    caseNote: s.caseNote,
    statuteNote: s.statuteNote,
    regulationNote: s.regulationNote,
  };
}

export default function McgillGuideCitationGeneratorWidget() {
  const strings = useToolUi(MCGILL_GUIDE_CITATION_GENERATOR_STRINGS);
  const [kind, setKind] = useState<McgillKind>("case");
  const fields = useMemo(() => fieldsFor(strings, kind), [strings, kind]);
  const labels = useMemo(() => mcgillLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatMcgill(kind, values, labels),
    [kind, labels]
  );
  const isReady = useCallback(
    (values: SourceValues) => mcgillIsReady(kind, values),
    [kind]
  );
  const builderStrings = useMemo(
    () => ({ ...strings, emptyPrompt: emptyPromptFor(strings, kind) }),
    [strings, kind]
  );

  return (
    <SourceCitationBuilder
      example={exampleFor(strings, kind)}
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.kindLabel}
          name="mcgill-kind"
          onChange={setKind}
          options={[
            { value: "case", label: strings.kindCase },
            { value: "statute", label: strings.kindStatute },
            { value: "regulation", label: strings.kindRegulation },
          ]}
          value={kind}
        />
      }
      isReady={isReady}
      strings={builderStrings}
    />
  );
}
